import { LitElement, html, nothing } from "lit";
import { packageReviewsStyles } from "js/sharedStyles/packageReviews.js";

class WJProviderReviews extends LitElement {
  static properties = {
    providerId: { type: Number, attribute: "data-provider-id" },
    productType: { type: String, attribute: "data-product-type" },
    pageSize: { type: Number, attribute: "data-page-size" },
    _reviews: { state: true },
    _pageNumber: { state: true },
    _totalCount: { state: true },
    _overallRating: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _hasMore: { state: true },
  };

  static styles = [packageReviewsStyles];

  constructor() {
    super();
    this.productType = "energy";
    this.pageSize = 10;
    this._reviews = [];
    this._pageNumber = 0;
    this._totalCount = 0;
    this._overallRating = 0;
    this._loading = false;
    this._error = false;
    this._hasMore = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.providerId > 0) this._loadMore();
  }

  async _loadMore() {
    if (this._loading) return;
    this._loading = true;
    this._error = false;
    const nextPage = this._pageNumber + 1;

    try {
      const url = `/api/provider/reviews?providerId=${this.providerId}&productType=${encodeURIComponent(this.productType)}&pageSize=${this.pageSize}&pageNumber=${nextPage}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const newReviews = Array.isArray(data?.ratings) ? data.ratings : [];
      this._reviews = [...this._reviews, ...newReviews];
      this._pageNumber = nextPage;
      this._totalCount = Number(data?.numberOfRatings) || this._reviews.length;
      this._overallRating = Number(data?.overallRating) || 0;
      this._hasMore = newReviews.length >= this.pageSize && this._reviews.length < this._totalCount;
    } catch {
      this._error = true;
    } finally {
      this._loading = false;
    }
  }

  _formatDate(raw) {
    if (!raw) return "";
    const datePart = String(raw).split("T")[0];
    const parts = datePart.split("-");
    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : datePart;
  }

  _renderReview(review, index) {
    const stars = Math.max(0, Math.min(5, Number(review.stars) || 0));
    const name = review.header || "Anonym";
    return html`
      <div class="${index === 0 ? "" : "u-border--top pt"} mb">
        <div class="u-size-s mb--">
          <strong class="mr-">${name}</strong>
          <span class="u-text-color-grey">${this._formatDate(review.lastModificationDate)}</span>
        </div>
        <div class="c-star-rating c-star-rating--small mb-">
          <span class="c-star-rating__empty"></span>
          <span class="c-star-rating__full" style="width: ${(stars / 5) * 100}%"></span>
        </div>
        ${review.text ? html`<div class="u-size-s">${review.text}</div>` : nothing}
      </div>
    `;
  }

  _renderSummary() {
    const ratingStr = (this._overallRating * 2).toFixed(1).replace(".", ",");
    const [ratingInt, ratingDec] = ratingStr.split(",");

    return html`
      <div class="c-rating-wrapper mb+">
        <div class="u-bg-color-grey--x-light u-border-radius-s ph pv- mb">
          <div class="layout layout--middle layout--flush u-flex-wrap--nowrap mb--">
            <div class="layout__item">
              <div class="u-font-weight--bold">Durchschnittliche Bewertung<span class="c-icon-info"></span></div>
              <div class="u-size-s u-font-weight--bold u-text-color-grey">${this._totalCount} Bewertungen</div>
            </div>
            <div class="layout__item u-1/4 u-text-align--center pv-">
              <span class="c-rating-total c-rating-total--tab c-spec c-spec--amount">
                ${ratingInt}<span class="c-spec__decimals">,${ratingDec}</span>
                <span class="c-rating-total__svg"
                  ><svg viewBox="0 0 61 61" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M28.195 58.036l-1.345.559a5.354 5.354 0 01-6.175-1.522l-.93-1.12a6 6 0 00-4.083-2.143l-1.45-.13a5.354 5.354 0 01-4.76-4.217l-.304-1.424a6 6 0 00-2.619-3.794l-1.224-.789A5.354 5.354 0 013.05 37.51l.393-1.401a6 6 0 00-.556-4.577l-.717-1.267a5.354 5.354 0 01.767-6.314l1-1.059a6 6 0 001.634-4.31l-.046-1.456a5.354 5.354 0 013.613-5.234l1.377-.473a6 6 0 003.451-3.057l.635-1.31a5.354 5.354 0 015.632-2.956l1.439.221a6 6 0 004.477-1.103l1.171-.865a5.354 5.354 0 016.36 0l1.171.865a6 6 0 004.477 1.103l1.44-.221a5.354 5.354 0 015.63 2.956l.636 1.31a6 6 0 003.45 3.057l1.378.473a5.354 5.354 0 013.613 5.234l-.046 1.455a6 6 0 001.635 4.311l1 1.059a5.354 5.354 0 01.766 6.314l-.717 1.267a6 6 0 00-.556 4.577l.393 1.401a5.354 5.354 0 01-2.255 5.947l-1.224.789a6 6 0 00-2.62 3.794l-.303 1.424a5.354 5.354 0 01-4.76 4.218l-1.45.13a6 6 0 00-4.083 2.142l-.93 1.12a5.354 5.354 0 01-6.175 1.522l-1.345-.56a6 6 0 00-4.61 0z"
                      fill="#61A38D"
                      class="u-svg-fill"
                    ></path>
                    <circle stroke="#F7F7F7" cx="30.5" cy="30.5" r="22" stroke-width="2"></circle></svg
                ></span>
              </span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.providerId || this.providerId <= 0) {
      return html`<p>Für diesen Anbieter sind derzeit keine Bewertungsdaten verfügbar.</p>`;
    }

    const isInitialLoad = this._loading && this._reviews.length === 0;
    if (isInitialLoad) {
      return html`<bc-loading-icon data-inline="true"></bc-loading-icon>`;
    }

    const hasMore = this._hasMore && this._reviews.length < this._totalCount;
    const hasSummary = this._overallRating > 0 || this._totalCount > 0;

    return html`
      ${hasSummary ? this._renderSummary() : nothing}
      <div class="layout">
        <div class="layout__item u-19/24(l)">
          ${this._reviews.length === 0 && !this._loading && !this._error ? html`<p>Für diesen Anbieter sind derzeit keine Bewertungen verfügbar.</p>` : nothing} ${this._reviews.map((r, i) => this._renderReview(r, i))} ${this._error ? html`<p>Bewertungen konnten nicht geladen werden.</p>` : nothing}
          ${hasMore
            ? html`
                <div class="u-text-align--center mt">
                  ${this._loading
                    ? html`<bc-loading-icon data-inline="true"></bc-loading-icon>`
                    : html`<button type="button" class="c-button c-button--support c-button--small" @click=${this._loadMore}>Weitere Bewertungen laden</button>`}
                </div>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}

const initWJProviderReviews = () => {
  if (!window.customElements.get("wj-provider-reviews")) {
    window.customElements.define("wj-provider-reviews", WJProviderReviews);
  }
};

export { initWJProviderReviews };
